import { Command, Option } from "clipanion";
import { confirm } from "@inquirer/prompts";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { getAzAccount } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { parseContainerAppResourceId } from "../../lib/platform-update.js";
import { fetchManifest, type ManifestSource } from "../../lib/release-channel.js";
import { resolveReleaseContentWithAgents, fetchRepoTree, verifyPersonaTrees, type RepoTree } from "../../lib/release-content.js";
import { readStamp } from "../../lib/platform-stamp.js";
import { diffPlan, applyPlan, type ComponentKey, type ApplyPlan } from "../../lib/platform-converge.js";
import { buildConvergeDeps, assertCliVersionOk } from "../../lib/platform-converge-cli.js";
import { CLI_VERSION } from "../../lib/package-version.js";
import { colors, renderJson, renderTable, resolveOutputMode } from "../../lib/output.js";

export class PlatformUpdateCommand extends M8tCommand {
  static paths = [["platform", "update"]];
  static usage = Command.Usage({
    description: "Converge the whole platform (gateway, hosted agents, personas, infra) to a release manifest.",
    details:
      "Fetches a release manifest (the current channel by default), diffs it against the installed-state stamp, and converges each changed component via its own executor: rolls the gateway/hosted-agent images, re-versions prompt personas (compose-preserving), and records infra state. A deploy/ tree drift is reported but NOT applied unless --force-infra is passed. Use --check to preview, --only to converge a single component, --yes to skip the confirmation prompt.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  to = Option.String("--to", {
    description: "Pin the platform version to converge to (defaults to the current release channel).",
  });
  only = Option.String("--only", {
    description: "Converge only one component: infra | gateway | codingAgent | azureExecutor | personas | brainSeeds.",
  });
  check = Option.Boolean("--check", false);
  yes = Option.Boolean("--yes", false);
  skipInfra = Option.Boolean("--skip-infra", false);
  forceInfra = Option.Boolean("--force-infra", false);
  suffix = Option.String("--suffix", {
    description: "Required with --force-infra — the existing deployment's resource-name suffix (avoids provisioning duplicates).",
  });
  contentDir = Option.String("--content-dir", {
    description: "Path to a local m8t repo checkout (skips the tarball fetch + tree-drift check).",
  });
  manifestUrl = Option.String("--manifest-url", {
    description: "Fetch the release manifest from this URL instead of the release channel.",
  });
  manifestFile = Option.String("--manifest-file", {
    description: "Read the release manifest from this local file instead of the release channel.",
  });
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint URL. Disambiguates the project in a multi-project subscription.",
  });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };

    const to = typeof this.to === "string" ? this.to : undefined;
    const only = typeof this.only === "string" ? (this.only as ComponentKey) : undefined;
    const suffix = typeof this.suffix === "string" ? this.suffix : undefined;
    const contentDirFlag = typeof this.contentDir === "string" ? this.contentDir : undefined;
    const manifestUrl = typeof this.manifestUrl === "string" ? this.manifestUrl : undefined;
    const manifestFile = typeof this.manifestFile === "string" ? this.manifestFile : undefined;

    const gw = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup } = parseContainerAppResourceId(gw.containerAppResourceId);
    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;

    const onProgress = (m: string) => {
      log(colors.dim(m));
    };
    const onWarn = (w: string) => {
      log(colors.warn(w));
    };

    onProgress("resolving Foundry project…");
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    const source: ManifestSource = manifestFile ? { file: manifestFile } : manifestUrl ? { url: manifestUrl } : { channel: true, version: to };
    onProgress("fetching release manifest…");
    const manifest = await fetchManifest(source);

    assertCliVersionOk(manifest, CLI_VERSION, onWarn);

    const stamp = await readStamp({ credential, subscriptionId, resourceGroup });

    onProgress("resolving release content…");
    const contentDir = await resolveReleaseContentWithAgents(manifest, { contentDir: contentDirFlag });
    const tree: RepoTree = contentDirFlag ? { treeShaOf: () => undefined } : await fetchRepoTree(manifest.platform.commit);
    if (!contentDirFlag) {
      const mismatches = verifyPersonaTrees(manifest, tree);
      if (mismatches.length > 0) {
        log(colors.warn(`warning: ${mismatches.length.toString()} persona tree mismatch(es) vs the manifest.`));
      }
    }

    const plan = diffPlan(manifest, stamp, tree, { only, forceInfra: this.forceInfra, skipInfra: this.skipInfra });
    this.renderPlan(plan, mode);

    const deferredInfra = plan.skipped.find((s) => s.component === "infra" && s.reason === "deferred");
    if (deferredInfra) {
      log(
        colors.warn(
          `infra changed in ${plan.targetVersion} (deploy/ tree drift) — NOT applied by the CLI. Run 'm8t platform update --force-infra --suffix <n>' or let the update job converge it.`,
        ),
      );
    }

    if (this.check) {
      if (mode === "json") this.context.stdout.write(renderJson({ ...plan, checked: true, applied: false }) + "\n");
      return 0;
    }

    if (plan.actions.length === 0) {
      if (mode === "json") this.context.stdout.write(renderJson({ ...plan, applied: false }) + "\n");
      else log(colors.success("already up to date ✓"));
      return 0;
    }

    if (!this.yes && interactive) {
      const ok = await confirm({
        message: `Converge ${plan.actions.length.toString()} component(s) to ${plan.targetVersion}?`,
        default: true,
      });
      if (!ok) {
        log(colors.dim("aborted — no change."));
        return 0;
      }
    }

    const deps = await buildConvergeDeps({
      credential,
      subscriptionId,
      resourceGroup,
      contentDir,
      manifest,
      tree,
      project,
      gatewayResourceId: gw.containerAppResourceId,
      gatewayUrl: gw.gatewayUrl,
      suffix,
      onProgress,
      onWarn,
    });

    await applyPlan(plan, deps, { credential, subscriptionId, resourceGroup, contentDir, manifest, onProgress, suffix });

    if (mode === "json") this.context.stdout.write(renderJson({ ...plan, applied: true }) + "\n");
    else log(colors.success(`converged → ${plan.targetVersion} ✓`));
    return 0;
  }

  private renderPlan(plan: ApplyPlan, mode: "pretty" | "json"): void {
    if (mode === "json") return;
    const rows = [
      ...plan.actions.map((a) => ({
        component: a.personaName ? `personas:${a.personaName}` : a.component,
        from: a.from ?? "(none)",
        to: a.to,
        reason: a.reason,
      })),
      ...plan.skipped.map((s) => ({
        component: s.personaName ? `personas:${s.personaName}` : s.component,
        from: "-",
        to: "-",
        reason: s.reason,
      })),
    ];
    this.context.stdout.write(
      renderTable(rows, [
        { key: "component", label: "COMPONENT" },
        { key: "from", label: "FROM" },
        { key: "to", label: "TO" },
        { key: "reason", label: "REASON" },
      ]) + "\n",
    );
  }
}
